import { fromWireValue } from '../pages/control/serialGateway/factoryParams';

export const PMR171_FACTORY_GATE_PASSWORD = '666';

export const PMR171_FACTORY_CALIBRATION_MIN = 0x55;
export const PMR171_FACTORY_CALIBRATION_MAX = 0x65;

export interface Pmr171FactoryCalibrationEvent {
  deviceId: string;
  type: string;
  typeCode: number;
  rw: number | null;
  wireValue: number | null;
  band: number | null;
  paramId: string | null;
  timestamp: number;
}

export interface FactoryReadAllDoneEvent {
  deviceId: string;
  ok: boolean;
  busy: boolean;
  errors: string[];
  globalValues: Record<string, number>;
  timestamp: number;
}

export interface FactoryReadBandDoneEvent {
  deviceId: string;
  band: number | null;
  ok: boolean;
  busy: boolean;
  errors: string[];
  values: Record<string, number>;
  timestamp: number;
}

let factorySessionUnlocked = false;

export function isFactorySessionUnlocked(): boolean {
  return factorySessionUnlocked;
}

export function unlockFactorySession(): void {
  factorySessionUnlocked = true;
}

export function parseFactoryCommandTypeCode(type: string): number | null {
  const normalized = type.trim().toUpperCase().replace(/^0X/, '');
  if (!/^[0-9A-F]{1,2}$/.test(normalized)) return null;
  return Number.parseInt(normalized, 16);
}

export function isPmr171FactoryCalibrationType(type: string): boolean {
  const code = parseFactoryCommandTypeCode(type);
  if (code == null) return false;
  return (
    (code >= PMR171_FACTORY_CALIBRATION_MIN && code <= PMR171_FACTORY_CALIBRATION_MAX)
    || code === 0x6f
    || code === 0x70
    || code === 0x72
  );
}

export function factoryTypeHex(typeCode: number): string {
  return `0X${typeCode.toString(16).toUpperCase().padStart(2, '0')}`;
}

function readNumberRecord(value: unknown): Record<string, number> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  const result: Record<string, number> = {};
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (typeof item === 'number' && Number.isFinite(item)) {
      result[key] = Math.round(item);
    } else if (typeof item === 'string' && /^-?\d+$/.test(item.trim())) {
      result[key] = Number(item.trim());
    }
  }
  return result;
}

function readFiniteNumber(data: Record<string, unknown>, ...keys: string[]): number | null {
  for (const key of keys) {
    const value = data[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && /^-?\d+$/.test(value.trim())) return Number(value.trim());
  }
  return null;
}

export function parseFactoryCalibrationEvent(
  deviceId: string,
  type: string,
  data: Record<string, unknown>,
  timestamp: number,
): Pmr171FactoryCalibrationEvent | null {
  const typeCode = parseFactoryCommandTypeCode(type);
  if (typeCode == null || !isPmr171FactoryCalibrationType(type)) return null;
  const paramIdRaw = data.paramId;
  return {
    deviceId,
    type: factoryTypeHex(typeCode),
    typeCode,
    rw: readFiniteNumber(data, 'rw', 'readWrite'),
    wireValue: readFiniteNumber(data, 'value', 'calibration', 'enabled'),
    band: readFiniteNumber(data, 'band'),
    paramId: typeof paramIdRaw === 'string' && paramIdRaw.trim() ? paramIdRaw.trim() : null,
    timestamp,
  };
}

export function parseFactoryReadAllDoneEvent(
  deviceId: string,
  type: string,
  data: Record<string, unknown>,
  timestamp: number,
): FactoryReadAllDoneEvent | null {
  if (String(type).trim().toUpperCase() !== 'FACTORY_READ_ALL_DONE') return null;
  const errorsRaw = data.errors;
  const errors = Array.isArray(errorsRaw)
    ? errorsRaw.filter((item): item is string => typeof item === 'string')
    : [];
  return {
    deviceId,
    ok: data.ok === true,
    busy: data.busy === true,
    errors,
    globalValues: readNumberRecord(data.globalValues),
    timestamp,
  };
}

export function parseFactoryReadBandDoneEvent(
  deviceId: string,
  type: string,
  data: Record<string, unknown>,
  timestamp: number,
): FactoryReadBandDoneEvent | null {
  if (String(type).trim().toUpperCase() !== 'FACTORY_READ_BAND_DONE') return null;
  const errorsRaw = data.errors;
  const errors = Array.isArray(errorsRaw)
    ? errorsRaw.filter((item): item is string => typeof item === 'string')
    : [];
  const bandRaw = data.band;
  const band =
    typeof bandRaw === 'number' && Number.isFinite(bandRaw)
      ? bandRaw
      : typeof bandRaw === 'string' && /^-?\d+$/.test(bandRaw.trim())
        ? Number(bandRaw.trim())
        : null;
  return {
    deviceId,
    band,
    ok: data.ok === true,
    busy: data.busy === true,
    errors,
    values: readNumberRecord(data.values),
    timestamp,
  };
}

export function factoryActualFromWire(typeCode: number, wire: number, _rw: number | null): number {
  if ((typeCode & 0xff) === 0x65) {
    return wire ? 1 : 0;
  }
  return fromWireValue(typeCode, wire);
}
